import React, { Component, createRef, RefObject } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import _isEqual from 'lodash/isEqual';

/**
 * @typedef {DateSeriesProps}
 * @property {number} bulletThreshold length of data above which bullets will not be shown
 * @property {DateData<D>} data array of objects that have a `date` property which is a Date instance
 * @property {DateDataFields} dateDataFields config for looking up and labeling the y-axis value on
 * each data object
 * @property {number} height height for the chart in pixels
 * @property {string} numberFormat a number formatting string - see docs:
 * https://www.amcharts.com/docs/v4/concepts/formatters/formatting-numbers/
 * @property {function} onChartReady optional callback invoked when chart is ready
 * @property {string} tooltipText text for tooltips that can include placeholders - see docs:
 * https://www.amcharts.com/docs/v4/concepts/tooltips/#Relation_to_data
 */
interface DateSeriesProps<D extends Record<string, unknown>> {
    bulletThreshold: number;
    data: DateData<D>[];
    dateDataFields: DateDataFields;
    height: number;
    numberFormat?: string;
    onChartReady?(): void;
    tooltipText: string;
}

/**
 * A line chart showing a series of data where the x-axis is represented by Date values.
 * @param {DateSeriesProps} props
 */
export class DateSeries<D extends Record<string, unknown>> extends Component<
    DateSeriesProps<D>
> {
    private chart: am4charts.XYChart | null = null;

    private divRef: RefObject<HTMLDivElement> | null = null;

    constructor(props: DateSeriesProps<D>) {
        super(props);

        this.divRef = createRef();
    }

    componentDidMount(): void {
        if (this.divRef?.current) {
            const {
                bulletThreshold,
                data,
                dateDataFields,
                numberFormat,
                onChartReady,
                tooltipText,
            } = this.props;
            const chart = am4core.create(
                this.divRef.current,
                am4charts.XYChart,
            );
            const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
            const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
            const lineSeries = chart.series.push(new am4charts.LineSeries());

            if (data.length < bulletThreshold) {
                const bullet = lineSeries.bullets.push(new am4charts.Bullet());
                const circle = bullet.createChild(am4core.Circle);

                circle.width = 5;
                circle.height = 5;
                circle.horizontalCenter = 'middle';
                circle.verticalCenter = 'middle';
                circle.tooltipText = tooltipText;
            }

            chart.data = data;

            if (numberFormat) {
                chart.numberFormatter.numberFormat = numberFormat;
            }

            chart.fontSize = 12;
            chart.padding(10, 30, 10, 20);
            chart.numberFormatter.bigNumberPrefixes = [
                { number: 1e3, suffix: 'K' },
                { number: 1e6, suffix: 'M' },
                { number: 1e9, suffix: 'B' },
                { number: 1e12, suffix: 'T' },
                { number: 1e15, suffix: 'Q' },
            ];
            chart.numberFormatter.smallNumberThreshold = 1e-12;
            chart.numberFormatter.smallNumberPrefixes = [
                { number: 1e-12, suffix: '' },
            ];

            dateAxis.renderer.minGridDistance = 40;
            dateAxis.cursorTooltipEnabled = false;
            dateAxis.startLocation = 0;
            dateAxis.endLocation = 0;
            dateAxis.baseInterval = {
                timeUnit: 'month',
                count: 3,
            };
            dateAxis.gridIntervals.setAll([
                { timeUnit: 'month', count: 3 },
                { timeUnit: 'year', count: 1 },
                { timeUnit: 'year', count: 2 },
                { timeUnit: 'year', count: 5 },
                { timeUnit: 'year', count: 10 },
            ]);
            dateAxis.dateFormats.setKey('month', 'Qq');
            dateAxis.periodChangeDateFormats.setKey('month', 'yyyy Qq');
            dateAxis.extraMax = 0.05;
            dateAxis.extraMin = 0.05;

            valueAxis.cursorTooltipEnabled = false;
            valueAxis.renderer.minGridDistance = 40;
            valueAxis.extraMin = 0.02;
            valueAxis.extraMax = 0.02;

            lineSeries.dataFields.valueY = dateDataFields.value;
            lineSeries.dataFields.dateX = 'date';
            lineSeries.dataItems.template.locations.dateX = 0;
            lineSeries.name = dateDataFields.label;
            lineSeries.strokeWidth = 2;

            if (lineSeries.tooltip) {
                lineSeries.tooltip.pointerOrientation = 'vertical';
            }

            chart.events.on('ready', () => {
                if (onChartReady) {
                    onChartReady();
                }
            });

            this.chart = chart;
        }
    }

    componentDidUpdate(prevProps: DateSeriesProps<D>): void {
        const { data } = this.props;

        if (!_isEqual(prevProps.data, data)) {
            if (this.chart !== null) {
                this.chart.data = data;
            }
        }
    }

    componentWillUnmount(): void {
        if (this.chart) {
            this.chart.dispose();
        }
    }

    render(): JSX.Element {
        const { height } = this.props;

        return (
            <div ref={this.divRef} className="w-full pr-4" style={{ height }} />
        );
    }
}
